import React, { useState } from "react";
import { Modal, Box, Typography, Grid, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { Document } from "../../Models/company";
import color from "../../Constants/colors";
import {
  useCoverSheet,
  useUploadCoverSheet,
  useUpdateCoverSheet,
} from "../../Query/useCompanies";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import FileUploaderAction from "../FileUpload/FileUploaderAction";
import FileUploader from "../FileUpload/FileUploader";
import { useTranslation } from "react-i18next";
import { allowedExtensionsCover } from "../../Constants/fileExtensions";
import { translateFieldName } from "../../Helpers/stringHelpers";

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
}

const CoverSheetUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation("documentUpload");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [page] = useState(0);
  const { companyId } = useParams<{ companyId: string }>();

  const { data: coverSheetData, isLoading: loadingCoverSheet } = useCoverSheet(
    companyId,
    open
  );

  const coverEntries = coverSheetData ? Object.entries(coverSheetData) : [];

  const uploadCoverSheetMutation = useUploadCoverSheet(companyId!);
  const updateCoverSheetMutation = useUpdateCoverSheet(companyId!);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");

  const handleFinishUpload = (name: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== name)
    );
  };

  const handleRemoveFile = (fileName: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName)
    );
  };

  const handleOpenFile = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  const handleConfirmEdit = async (key: string) => {
    if (!companyId) return;
    updateCoverSheetMutation.mutate(
      { [key]: editingValue },
      {
        onSuccess: () => {
          setEditingKey(null);
        },
      }
    );
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
  };

  const handleSubmitFiles = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    if (!companyId) return;

    const uploadPromises = uploadedFiles?.map(async (file) =>
      uploadCoverSheetMutation.mutateAsync(file)
    );

    Promise.all(uploadPromises)
      .then((responses) => {
        responses.forEach((_, index) => {
          handleFinishUpload(uploadedFiles[index].name);
        });
      })
      .catch(() => {});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: color.white,
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          width: "100%",
          maxWidth: "1100px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${color.gray200}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 600,
              color: color.primary_dark,
            }}
          >
            {t("documentUpload.uploadNewCover")}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              padding: "8px",
              borderRadius: "4px",
              "&:hover": { backgroundColor: color.gray100 },
            }}
          >
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Box>

        <Grid container sx={{ display: "flex", width: "100%", height: "100%" }}>
          <Box
            sx={{
              flex: "1 1 50%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "stretch",
              borderRight: `1px solid ${color.gray200}`,
              height: "100%",
              overflowY: "auto",
              padding: "24px",
              backgroundColor: color.gray50,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 600, color: color.primary_dark }}
            >
              {t("documentUpload.uploadNewCover")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                padding: 2,
                backgroundColor: color.white,
                borderRadius: "8px",
                border: `1px dashed ${color.gray300}`,
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FileUploader
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                allowedExtensions={allowedExtensionsCover}
                page={page}
                rowsPerPage={uploadedFiles?.length}
                handleRemoveFile={handleRemoveFile}
                handleOpenFile={handleOpenFile}
                showUploadImage={false}
                text={true}
              />
            </Box>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={3}
              mt={3}
            >
              <FileUploaderAction
                buttonTitle={t("documentUpload.clear")}
                tooltipTitle={t("documentUpload.clearTooltip_noFiles")}
                disableHoverListener={uploadedFiles?.length > 0}
                disabled={
                  uploadCoverSheetMutation.isPending ||
                  uploadedFiles?.length === 0
                }
                onClick={clearAllFiles}
              />
              <FileUploaderAction
                buttonTitle={t("documentUpload.submit")}
                tooltipTitle={
                  uploadedFiles?.length === 0
                    ? t("documentUpload.submitTooltip_noFiles")
                    : ""
                }
                disableHoverListener={uploadedFiles?.length > 0}
                disabled={
                  uploadCoverSheetMutation.isPending ||
                  uploadedFiles?.length === 0
                }
                onClick={handleSubmitFiles}
              />
            </Grid>
          </Box>

          <Box
            sx={{
              flex: "1 1 50%",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "stretch",
              height: "100%",
              padding: "24px",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mb: 1, fontWeight: 600, color: color.primary_dark }}
            >
              {t("documentUpload.coverSheetDetails")}
            </Typography>

            <Box
              sx={{
                overflowY: "auto",
                width: "100%",
                flex: 1,
                padding: 2.5,
                borderRadius: "8px",
                border: `1px solid ${color.gray200}`,
                backgroundColor: color.white,
              }}
            >
              {loadingCoverSheet ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: color.gray600,
                    padding: 4,
                  }}
                >
                  {t("documentUpload.loadingCoverSheet")}
                </Typography>
              ) : coverEntries.length > 0 ? (
                <Grid container direction="column" spacing={1.5}>
                  {coverEntries.map(([key, value]) => (
                    <Grid
                      item
                      xs={12}
                      key={key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "180px 1fr",
                        gap: 2,
                        alignItems: "start",
                        padding: "12px 8px",
                        borderBottom: `1px solid ${color.gray200}`,
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: color.primary_dark,
                          whiteSpace: "normal",
                        }}
                      >
                        {translateFieldName(key, t)}:
                      </Typography>
                      {editingKey === key ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <input
                            type="text"
                            value={editingValue ?? ""}
                            onChange={(e) => setEditingValue(e.target.value)}
                            style={{
                              fontSize: "0.95rem",
                              fontWeight: 400,
                              color: color.primary_dark,
                              border: `1px solid ${color.gray300}`,
                              borderRadius: 6,
                              padding: "6px 8px",
                              boxSizing: "border-box",
                              outline: "none",
                              marginLeft: "auto",
                              textAlign: "left",
                              width: "100%",
                              maxWidth: "100%",
                            }}
                          />
                          <IconButton
                            onClick={() => handleConfirmEdit(key)}
                            size="small"
                          >
                            <CheckIcon
                              fontSize="small"
                              sx={{ color: color.green }}
                            />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: color.primary_dark,
                              textAlign: "left",
                              flexGrow: 1,
                              overflow: "visible",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {String(value)}
                          </Typography>
                          {value && String(value).trim() !== "" && (
                            <IconButton
                              onClick={() => {
                                setEditingKey(key);
                                setEditingValue(String(value));
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: color.gray600,
                    padding: 4,
                  }}
                >
                  {t("documentUpload.noUploadedCoverSheet")}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Box>
    </Modal>
  );
};

export default CoverSheetUploadModal;
